import { useMemo, useState } from "react";
import { Link } from "@tanstack/react-router";
import { ArrowRight, Check, ChevronDown } from "lucide-react";
import { Header } from "./Header";
import { Footer } from "./Footer";
import { LoanWarning } from "./LoanWarning";
import { StarRating } from "./StarRating";
import { LenderLogo } from "./LenderLogo";
import { LENDERS } from "@/lib/lenders";
import { formatKr, monthlyCost, type PseoLender } from "@/lib/pseo";
import { effectiveRate } from "@/lib/pseo-cost";
import type { EditorialBlock } from "@/lib/pseo-editorial";
import { useUpdatedStamp } from "@/lib/useCurrentMonth";
import { EpiProvider, useTrackUrl } from "@/lib/epi";

const TERMS = [1, 2, 3, 5, 8, 12];

function CostTable({ amount, rate }: { amount: number; rate: number }) {
  return (
    <div className="mt-6 overflow-x-auto rounded-md border border-border bg-card">
      <table className="w-full min-w-[520px] text-left text-sm">
        <caption className="sr-only">Månadskostnad och totalkostnad för olika löptider</caption>
        <thead className="bg-surface text-xs uppercase tracking-wide text-muted-foreground">
          <tr>
            <th scope="col" className="px-4 py-3 font-semibold">Löptid</th>
            <th scope="col" className="px-4 py-3 font-semibold">Månadskostnad</th>
            <th scope="col" className="px-4 py-3 font-semibold">Totalt att betala</th>
            <th scope="col" className="px-4 py-3 font-semibold">Räntekostnad</th>
          </tr>
        </thead>
        <tbody>
          {TERMS.map((y) => {
            const m = monthlyCost(amount, y, rate);
            const total = m * y * 12;
            return (
              <tr key={y} className="border-t border-border">
                <th scope="row" className="px-4 py-3 font-semibold text-foreground">{y} år</th>
                <td className="px-4 py-3 text-foreground">{formatKr(m)}/mån</td>
                <td className="px-4 py-3 text-muted-foreground">{formatKr(total)}</td>
                <td className="px-4 py-3 text-muted-foreground">{formatKr(total - amount)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function parseFee(value?: string) {
  if (!value) return 0;
  const match = value.replace(/\s/g, "").replace(",", ".").match(/\d+(?:\.\d+)?/);
  return match ? Number(match[0]) : 0;
}

function formatPercent(value: number) {
  return value.toFixed(2).replace(".", ",") + " %";
}

function formatStoredPercent(value?: string) {
  if (!value) return null;
  return value.replace(/\s*%$/, " %");
}

function effectiveRateLabel(lender: PseoLender, amount: number, years: number) {
  const base = LENDERS[lender.id];
  const stored = formatStoredPercent(lender.effRate ?? base?.effRate);
  if (stored) return stored;
  const setupFee = parseFee(base?.setupFee);
  const monthlyFee = parseFee(base?.adminFee) + parseFee(base?.invoiceFee);
  return formatPercent(effectiveRate(amount, years * 12, lender.minInterest, setupFee, monthlyFee));
}

function AffiliateDisclosure() {
  return (
    <p className="rounded-md border border-border bg-card p-4 text-xs leading-relaxed text-muted-foreground">
      Svensk Låneguide är en oberoende jämförelsetjänst. Vi får ersättning från långivare när du ansöker via
      våra länkar. Ersättningen påverkar inte vilka villkor du får och inte heller vår bedömning – vi redovisar
      både för- och nackdelar för varje långivare.
    </p>
  );
}

function ComparisonTable({
  lenders,
  amount,
  years,
  showEffectiveRate = false,
}: {
  lenders: PseoLender[];
  amount: number;
  years: number;
  showEffectiveRate?: boolean;
}) {
  return (
    <div className="mt-6 overflow-x-auto rounded-md border border-border bg-card">
      <table className={`w-full text-left text-sm ${showEffectiveRate ? "min-w-[840px]" : "min-w-[720px]"}`}>
        <caption className="sr-only">Jämförelse av matchande långivare</caption>
        <thead className="bg-surface text-xs uppercase tracking-wide text-muted-foreground">
          <tr>
            <th scope="col" className="px-4 py-3 font-semibold">Långivare</th>
            <th scope="col" className="px-4 py-3 font-semibold">Belopp</th>
            <th scope="col" className="px-4 py-3 font-semibold">Ränta från</th>
            {showEffectiveRate ? <th scope="col" className="px-4 py-3 font-semibold">Effektiv ränta</th> : null}
            <th scope="col" className="px-4 py-3 font-semibold">Kreditupplysning</th>
            <th scope="col" className="px-4 py-3 font-semibold">Anmärkning</th>
            <th scope="col" className="px-4 py-3 font-semibold">Ca månadskostnad</th>
          </tr>
        </thead>
        <tbody>
          {lenders.map((l) => (
            <tr key={l.id} className="border-t border-border">
              <th scope="row" className="px-4 py-3 font-semibold text-foreground">{l.name}</th>
              <td className="px-4 py-3 text-muted-foreground">
                {formatKr(l.minAmount)} – {formatKr(l.maxAmount)}
              </td>
              <td className="px-4 py-3 text-foreground">{l.minInterest.toString().replace(".", ",")} %</td>
              {showEffectiveRate ? <td className="px-4 py-3 text-foreground">{effectiveRateLabel(l, amount, years)}</td> : null}
              <td className="px-4 py-3 text-muted-foreground">{l.allowsUC ? "UC" : "Ej UC"}</td>
              <td className="px-4 py-3 text-muted-foreground">{l.allowsBadCredit ? "Kan godkännas" : "Nej"}</td>
              <td className="px-4 py-3 text-foreground">
                {formatKr(monthlyCost(amount, years, l.minInterest + 4))}/mån
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}


function ApplyButton({ lender }: { lender: PseoLender }) {
  const track = useTrackUrl();
  return (
    <a
      href={track(lender.affiliateUrl, "pseo-lender-card")}
      target="_blank"
      rel="sponsored nofollow noopener"
      className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-md bg-cta px-5 text-sm font-bold !text-cta-foreground !no-underline transition hover:opacity-90"
    >
      Ansök nu <ArrowRight className="h-4 w-4" />
    </a>
  );
}

function LenderCard({ lender, amount, years }: { lender: PseoLender; amount: number; years: number }) {
  const base = LENDERS[lender.id];
  const monthly = monthlyCost(amount, years, lender.minInterest + 4);
  const [open, setOpen] = useState(false);
  return (
    <article className="flex flex-col gap-4 rounded-md border border-border bg-card p-5 shadow-sm">
      <div className="flex items-center gap-4">
        {base ? (
          <LenderLogo lender={base} size={56} bare />
        ) : (
          <div className="grid h-14 w-14 place-items-center rounded-md bg-primary font-display font-black text-primary-foreground">
            {lender.name.slice(0, 2).toUpperCase()}
          </div>
        )}
        <div className="min-w-0">
          <h3 className="truncate font-display text-lg font-bold text-foreground">{lender.name}</h3>
          <StarRating rating={lender.rating} />
        </div>
      </div>
      <p className="text-sm leading-relaxed text-muted-foreground">{lender.description}</p>
      <dl className="grid grid-cols-2 gap-x-4 gap-y-2 border-t border-border pt-4 text-sm">
        <dt className="text-muted-foreground">Belopp</dt>
        <dd className="text-right font-semibold text-foreground">
          {formatKr(lender.minAmount)} – {formatKr(lender.maxAmount)}
        </dd>
        <dt className="text-muted-foreground">Ränta från</dt>
        <dd className="text-right font-semibold text-foreground">
          {lender.minInterest.toString().replace(".", ",")} %
        </dd>
        <dt className="text-muted-foreground">Kreditupplysning</dt>
        <dd className="text-right font-semibold text-foreground">{lender.allowsUC ? "UC" : "Ej UC"}</dd>
        <dt className="text-muted-foreground">Betalningsanmärkning</dt>
        <dd className="text-right font-semibold text-foreground">
          {lender.allowsBadCredit ? "Kan godkännas" : "Godkänns ej"}
        </dd>
        <dt className="text-muted-foreground">Ca månadskostnad</dt>
        <dd className="text-right font-semibold text-foreground">{formatKr(monthly)}/mån</dd>
      </dl>
      <div className="mt-auto flex flex-col gap-2 pt-2">
        <ApplyButton lender={lender} />
        <button
          onClick={() => setOpen(!open)}
          className="flex items-center justify-center gap-1 text-xs font-semibold text-primary hover:underline"
        >
          Läs mer <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
        </button>
      </div>
      {open && base && (
        <div className="-mx-5 -mb-5 mt-2 border-t border-border bg-muted/40 px-5 py-4 text-xs leading-relaxed text-muted-foreground">
          <div className="mb-1 font-semibold text-foreground">Avgifter</div>
          <dl className="mb-3 divide-y divide-border border-y border-border">
            <div className="flex items-center justify-between py-1.5">
              <dt>Uppläggningsavgift</dt>
              <dd className="font-bold text-foreground">{base.setupFee}</dd>
            </div>
            <div className="flex items-center justify-between py-1.5">
              <dt>Administrationsavgift</dt>
              <dd className="font-bold text-foreground">{base.adminFee}</dd>
            </div>
            <div className="flex items-center justify-between py-1.5">
              <dt>Fakturaavgift</dt>
              <dd className="font-bold text-foreground">{base.invoiceFee}</dd>
            </div>
          </dl>
          <div className="mb-1 font-semibold text-foreground">Räkneexempel</div>
          <p className="italic">{base.example}</p>
          <div className="mt-3 flex flex-wrap items-center gap-4">
            <Link
              to="/privatlan/$slug"
              params={{ slug: base.slug }}
              className="text-[13px] font-semibold text-primary underline underline-offset-2 hover:text-primary/80"
            >
              Mer information om {lender.name}
            </Link>
            {base.extraLink && (
              <div className="w-full">
                {base.extraLink.description && (
                  <p className="mb-1 text-xs leading-relaxed text-muted-foreground">{base.extraLink.description}</p>
                )}
                <a
                  href={base.extraLink.href}
                  target="_blank"
                  rel="noreferrer"
                  className="text-[13px] font-semibold text-primary underline underline-offset-2 hover:text-primary/80"
                >
                  {base.extraLink.label}
                </a>
              </div>
            )}
          </div>
        </div>
      )}
    </article>
  );
}

export type PseoCrumb = { label: string; to?: string };
export type PseoLink = { label: string; to: string };
export type PseoByline = {
  author: string;
  authorRole: string;
  authorHref?: string;
  reviewer?: string;
  reviewerRole?: string;
};
export type PseoSource = { label: string; href: string };

export function PseoPage({
  h1,
  intro,
  amount,
  onAmountChange,
  lenders,
  amountEditable = false,
  extraContent,
  crumbs,
  faq,
  relatedTitle,
  related,
  editorial,
  tablesLabel,
  costHeading,
  comparisonHeading,
  comparisonDataDate,
  showEffectiveRateInComparison = false,
  affiliateDisclosurePlacement = "sources",
  epi = "pseo",
  leadAnswer,
  byline,
  sources,
  defaultYears = 5,
}: {
  h1: string;
  intro: string;
  amount: number;
  onAmountChange?: (n: number) => void;
  lenders: PseoLender[];
  amountEditable?: boolean;
  extraContent?: React.ReactNode;
  crumbs?: PseoCrumb[];
  faq?: { q: string; a: string }[];
  relatedTitle?: string;
  related?: PseoLink[];
  editorial?: EditorialBlock[];
  tablesLabel?: string;
  costHeading?: React.ReactNode;
  comparisonHeading?: React.ReactNode;
  comparisonDataDate?: string;
  showEffectiveRateInComparison?: boolean;
  affiliateDisclosurePlacement?: "sources" | "comparison";
  epi?: string;
  /** Kort, komplett svar högst upp – före kalkylator och tabeller. */
  leadAnswer?: React.ReactNode;
  byline?: PseoByline;
  sources?: PseoSource[];
  defaultYears?: number;
}) {
  const [years, setYears] = useState(defaultYears);
  const updated = useUpdatedStamp();
  const best = useMemo(
    () => (lenders.length ? Math.min(...lenders.map((l) => l.minInterest)) : 0),
    [lenders],
  );
  const monthly = lenders.length ? monthlyCost(amount, years, best + 4) : 0;
  const label = tablesLabel ?? formatKr(amount);


  return (
    <EpiProvider value={epi}>
      <div className="min-h-screen">
      <Header />
      <main>
        <section className="border-b border-border bg-surface py-12">
          <div className="mx-auto grid max-w-7xl gap-10 px-4 sm:px-6 lg:grid-cols-2 lg:px-8">
            <div>
              {crumbs && crumbs.length > 0 ? (
                <nav aria-label="Brödsmulor" className="mb-4 text-xs text-muted-foreground">
                  <ol className="flex flex-wrap items-center gap-1">
                    {crumbs.map((c, i) => (
                      <li key={c.label} className="flex items-center gap-1">
                        {i > 0 ? <span aria-hidden="true">/</span> : null}
                        {c.to ? (
                          <Link to={c.to} className="text-primary underline underline-offset-2">
                            {c.label}
                          </Link>
                        ) : (
                          <span>{c.label}</span>
                        )}
                      </li>
                    ))}
                  </ol>
                </nav>
              ) : null}
              <h1 className="font-display text-3xl font-black leading-tight text-foreground sm:text-4xl">{h1}</h1>
              {byline ? (
                <p className="mt-3 text-xs leading-relaxed text-muted-foreground">
                  Skriven av{" "}
                  {byline.authorHref ? (
                    <Link to={byline.authorHref} className="font-semibold text-primary underline underline-offset-2">
                      {byline.author}
                    </Link>
                  ) : (
                    <span className="font-semibold text-foreground">{byline.author}</span>
                  )}
                  , {byline.authorRole}.
                  {byline.reviewer ? (
                    <>
                      {" "}
                      Faktagranskad av <span className="font-semibold text-foreground">{byline.reviewer}</span>
                      {byline.reviewerRole ? `, ${byline.reviewerRole}` : ""}.
                    </>
                  ) : null}{" "}
                  Senast uppdaterad {updated}.
                </p>
              ) : (
                <p className="mt-2 text-xs text-muted-foreground">Senast uppdaterad: {updated}</p>
              )}
              <p className="mt-4 max-w-xl text-[15px] leading-relaxed text-muted-foreground">{intro}</p>
              {leadAnswer ? (
                <div className="mt-5 max-w-xl rounded-md border border-primary/30 bg-card p-5">
                  <h2 className="font-display text-base font-bold text-foreground">Kort svar</h2>
                  <div className="mt-2 space-y-3 text-[15px] leading-relaxed text-foreground/80">{leadAnswer}</div>
                </div>
              ) : null}


              <ul className="mt-6 space-y-2 text-sm text-foreground">
                {[
                  `${lenders.length} granskade långivare matchar ditt val`,
                  "Kostnadsfritt och utan förpliktelser",
                  "Jämför ränta, belopp och villkor på en minut",
                ].map((t) => (
                  <li key={t} className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-primary" /> {t}
                  </li>
                ))}
              </ul>
              <a
                href="#toplist"
                className="mt-7 inline-flex h-12 items-center justify-center rounded-md bg-primary px-6 text-sm font-bold text-primary-foreground no-underline transition hover:opacity-90"
              >
                Se hela topplistan
              </a>
            </div>

            <div className="self-start rounded-md border border-border bg-card p-6 shadow-sm">
              <h2 className="font-display text-lg font-bold text-foreground">Lånekalkylator</h2>
              <label className="mt-5 block text-sm font-semibold text-foreground">
                Lånebelopp
                <input
                  type="range"
                  min={5000}
                  max={600000}
                  step={5000}
                  value={amount}
                  disabled={!amountEditable}
                  onChange={(e) => onAmountChange?.(Number(e.target.value))}
                  className="mt-3 w-full accent-primary disabled:opacity-60"
                />
                <span className="mt-1 block font-display text-2xl font-black text-primary">{formatKr(amount)}</span>
              </label>
              <label className="mt-5 block text-sm font-semibold text-foreground">
                Återbetalningstid: {years} år
                <input
                  type="range"
                  min={1}
                  max={15}
                  step={1}
                  value={years}
                  onChange={(e) => setYears(Number(e.target.value))}
                  className="mt-3 w-full accent-primary"
                />
              </label>
              <div className="mt-6 rounded-md border border-border p-4">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">Uppskattad månadskostnad</p>
                <p className="font-display text-3xl font-black text-primary">{formatKr(monthly)}</p>
                <p className="mt-1 text-xs text-muted-foreground">
                  Exempel baserat på bästa ränta bland matchande långivare. Din ränta sätts individuellt.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section id="toplist" className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-black text-foreground">Långivare som matchar {formatKr(amount)}</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            Endast långivare vars belopps­gränser rymmer ditt val visas nedan.
          </p>
          {lenders.length === 0 ? (
            <p className="mt-8 rounded-md border border-border bg-card p-6 text-sm text-muted-foreground">
              Ingen av våra långivare erbjuder just detta belopp. Prova ett annat belopp eller se{" "}
              <Link to="/privatlan" className="text-primary underline underline-offset-2">
                alla privatlån
              </Link>
              .
            </p>
          ) : (
            <div className="mt-8 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {lenders.map((l) => (
                <LenderCard key={l.id} lender={l} amount={amount} years={years} />
              ))}
            </div>
          )}
          <LoanWarning className="mt-8" />
        </section>

        {lenders.length > 0 ? (
          <section className="border-t border-border bg-surface py-14">
            <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
              <h2 className="font-display text-2xl font-black text-foreground">
                {costHeading ?? <>Så mycket kostar {label} per månad</>}
              </h2>
              <p className="mt-2 text-sm text-muted-foreground">
                Beräknat på {best.toString().replace(".", ",")} % ränta, som är den lägsta räntan bland långivarna
                ovan. Avgifter tillkommer och din ränta sätts individuellt.
              </p>
              <CostTable amount={amount} rate={best} />

              <h2 className="mt-12 font-display text-2xl font-black text-foreground">
                {comparisonHeading ?? <>Jämför långivare för {label}</>}
              </h2>
              <p className="mt-2 text-sm text-muted-foreground">
                Månadskostnaden nedan avser {years} års återbetalningstid.
              </p>
              {affiliateDisclosurePlacement === "comparison" ? (
                <div className="mt-5">
                  <AffiliateDisclosure />
                </div>
              ) : null}
              {comparisonDataDate ? (
                <p className="mt-5 text-sm font-semibold text-foreground">
                  Uppgifterna i jämförelsetabellen avser {comparisonDataDate}.
                </p>
              ) : null}
              <ComparisonTable lenders={lenders} amount={amount} years={years} showEffectiveRate={showEffectiveRateInComparison} />
            </div>
          </section>
        ) : null}

        {editorial && editorial.length > 0 ? (
          <section className="mx-auto max-w-3xl px-4 py-14 sm:px-6 lg:px-8">
            {editorial.map((b) => (
              <div key={b.heading} className="mt-8 first:mt-0">
                <h2 className="font-display text-2xl font-black text-foreground">{b.heading}</h2>
                {b.paragraphs.map((p) => (
                  <p key={p.slice(0, 40)} className="mt-3 text-[15px] leading-relaxed text-muted-foreground">
                    {p}
                  </p>
                ))}
              </div>
            ))}
          </section>
        ) : null}



        {extraContent}

        {faq && faq.length > 0 ? (
          <section className="border-t border-border bg-surface py-14">
            <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
              <h2 className="font-display text-2xl font-black text-foreground">Vanliga frågor</h2>
              <dl className="mt-6 space-y-4">
                {faq.map((f) => (
                  <div key={f.q} className="rounded-md border border-border bg-card p-5">
                    <dt className="font-display text-base font-bold text-foreground">{f.q}</dt>
                    <dd className="mt-2 text-[15px] leading-relaxed text-muted-foreground">{f.a}</dd>
                  </div>
                ))}
              </dl>
            </div>
          </section>
        ) : null}

        {sources && sources.length > 0 ? (
          <section className="mx-auto max-w-3xl px-4 py-12 sm:px-6 lg:px-8">
            <h2 className="font-display text-xl font-black text-foreground">Källor</h2>
            <ul className="mt-4 space-y-2 text-sm text-muted-foreground">
              {sources.map((s) => (
                <li key={s.href}>
                  <a
                    href={s.href}
                    target="_blank"
                    rel="noopener"
                    className="text-primary underline underline-offset-2"
                  >
                    {s.label}
                  </a>
                </li>
              ))}
            </ul>
            {affiliateDisclosurePlacement === "sources" ? (
              <div className="mt-4">
                <AffiliateDisclosure />
              </div>
            ) : null}
          </section>
        ) : null}



        {related && related.length > 0 ? (
          <section className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
            <h2 className="font-display text-xl font-black text-foreground">
              {relatedTitle ?? "Relaterade sidor"}
            </h2>
            <ul className="mt-4 flex flex-wrap gap-2">
              {related.map((r) => (
                <li key={r.to}>
                  <Link
                    to={r.to}
                    className="inline-flex items-center rounded-md border border-border bg-card px-3 py-2 text-sm text-foreground no-underline transition hover:border-primary hover:text-primary"
                  >
                    {r.label}
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        ) : null}

      </main>
      <Footer />
      </div>
    </EpiProvider>
  );
}
